import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { startAuthentication } from '@simplewebauthn/browser'
import { useOptimistic, useState, useTransition } from 'react'
import { data, Form, Link, useNavigate, useSearchParams } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { motion } from 'framer-motion'
import { Home, User, Lock, ArrowRight, Shield, Sparkles, Building, CheckCircle } from 'lucide-react'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList } from '#app/components/forms.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Input } from '#app/components/ui/input.tsx'
import { Label } from '#app/components/ui/label.tsx'
import { Checkbox } from '#app/components/ui/checkbox.tsx'
import { login, requireAnonymous } from '#app/utils/auth.server.ts'
import {
	ProviderConnectionForm,
	providerNames,
} from '#app/utils/connections.tsx'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { getErrorMessage, useIsPending } from '#app/utils/misc.tsx'
import { PasswordSchema, UsernameSchema } from '#app/utils/user-validation.ts'
import { type Route } from './+types/login.ts'
import { handleNewSession } from './login.server.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const LoginFormSchema = z.object({
	username: UsernameSchema,
	password: PasswordSchema,
	redirectTo: z.string().optional(),
	remember: z.boolean().optional(),
})

const AuthenticationOptionsSchema = z.object({
	options: z.object({ challenge: z.string() }),
}) satisfies z.ZodType<{ options: PublicKeyCredentialRequestOptionsJSON }>

export async function loader({ request }: Route.LoaderArgs) {
	await requireAnonymous(request)
	return {}
}

export async function action({ request }: Route.ActionArgs) {
	await requireAnonymous(request)
	const formData = await request.formData()
	await checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: (intent) =>
			LoginFormSchema.transform(async (data, ctx) => {
				if (intent !== null) return { ...data, session: null }

				const session = await login(data)
				if (!session) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Ongeldige gebruikersnaam of wachtwoord',
					})
					return z.NEVER
				}

				return { ...data, session }
			}),
		async: true,
	})

	if (submission.status !== 'success' || !submission.value.session) {
		return data(
			{ result: submission.reply({ hideFields: ['password'] }) },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { session, remember, redirectTo } = submission.value

	return handleNewSession({
		request,
		session,
		remember: remember ?? false,
		redirectTo,
	})
}

const testimonials = [
	{
		quote: "Woonplan heeft onze renovatie van begin tot eind begeleid. De AI-aanbevelingen hebben ons duizenden euro's bespaard!",
		author: "Jan Bakker",
		location: "Rotterdam",
		project: "Keukenrenovatie"
	},
	{
		quote: "Het platform is zo intuïtief. Binnen een week hadden we al drie offertes van betrouwbare aannemers.",
		author: "Lisa van den Berg",
		location: "Amsterdam",
		project: "Badkamer verbouwing"
	},
	{
		quote: "De energie-besparingen die Woonplan voorstelde hebben onze maandlasten met 30% verlaagd.",
		author: "Mohamed El Fassi",
		location: "Utrecht",
		project: "Duurzaamheidsupgrade"
	}
]

const stats = [
	{ value: "10.000+", label: "Tevreden gebruikers" },
	{ value: "€2.1M", label: "Bespaard door onze gebruikers" },
	{ value: "4.9/5", label: "Gemiddelde beoordeling" },
]

export default function LoginPage({ actionData }: Route.ComponentProps) {
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')
	const [currentTestimonial, setCurrentTestimonial] = useState(0)

	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getZodConstraint(LoginFormSchema),
		defaultValue: { redirectTo },
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LoginFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	// Auto-rotate testimonials
	useState(() => {
		const interval = setInterval(() => {
			setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)
		}, 5000)
		return () => clearInterval(interval)
	})

	return (
		<div className="min-h-screen flex">
			{/* Left Panel - Login Form */}
			<div className="flex-1 flex items-center justify-center px-8 bg-gradient-to-br from-gray-50 to-gray-100/50">
				<motion.div
					initial={{ opacity: 0, x: -20 }}
					animate={{ opacity: 1, x: 0 }}
					transition={{ duration: 0.5 }}
					className="w-full max-w-md space-y-8"
				>
					{/* Logo */}
					<div className="flex items-center gap-3 justify-center">
						<div className="p-2 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl shadow-lg">
							<Home className="h-8 w-8 text-white" />
						</div>
						<div className="grid leading-tight">
							<span className="text-2xl font-light text-gray-700">woon</span>
							<span className="text-2xl font-bold text-gray-900">plan</span>
						</div>
					</div>

					<div className="space-y-2 text-center">
						<h1 className="text-3xl font-bold tracking-tight">
							Welkom terug
						</h1>
						<p className="text-gray-600">
							Log in om verder te gaan met je renovatieproject
						</p>
					</div>

					{/* Quick Stats for mobile */}
					<div className="grid grid-cols-3 gap-4 lg:hidden">
						{stats.map((stat, i) => (
							<motion.div
								key={stat.label}
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: i * 0.1 }}
								className="text-center"
							>
								<p className="text-lg font-bold text-gray-900">{stat.value}</p>
								<p className="text-xs text-gray-600">{stat.label}</p>
							</motion.div>
						))}
					</div>

					<Form method="POST" {...getFormProps(form)} className="space-y-6">
						<HoneypotInputs />

						<div className="space-y-2">
							<Label htmlFor={fields.username.id}>
								Gebruikersnaam
							</Label>
							<div className="relative">
								<User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
								<Input
									{...getInputProps(fields.username, { type: 'text' })}
									autoFocus
									className="pl-10 h-11 lowercase"
									autoComplete="username"
									placeholder="jouw.naam"
								/>
							</div>
							<ErrorList errors={fields.username.errors} id={fields.username.errorId} />
						</div>

						<div className="space-y-2">
							<div className="flex items-center justify-between">
								<Label htmlFor={fields.password.id}>
									Wachtwoord
								</Label>
								<Link
									to="/forgot-password"
									className="text-sm font-medium text-blue-600 hover:text-blue-500"
								>
									Wachtwoord vergeten?
								</Link>
							</div>
							<div className="relative">
								<Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
								<Input
									{...getInputProps(fields.password, { type: 'password' })}
									className="pl-10 h-11"
									autoComplete="current-password"
									placeholder="••••••••"
								/>
							</div>
							<ErrorList errors={fields.password.errors} id={fields.password.errorId} />
						</div>

						<div className="flex items-center space-x-2">
							<Checkbox
								name={fields.remember.name}
								defaultChecked={fields.remember.initialValue === 'on'}
								id={fields.remember.id}
							/>
							<Label
								htmlFor={fields.remember.id}
								className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
							>
								Onthoud mij
							</Label>
						</div>

						<ErrorList errors={form.errors} id={form.errorId} />

						<Button
							className="w-full h-11 text-base font-medium bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
							type="submit"
							disabled={isPending}
						>
							{isPending ? (
								<>
									<span className="mr-2">Inloggen...</span>
									<motion.span
										animate={{ rotate: 360 }}
										transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
									>
										⏳
									</motion.span>
								</>
							) : (
								<>
									Inloggen
									<ArrowRight className="ml-2 h-4 w-4" />
								</>
							)}
						</Button>
					</Form>

					<div className="relative">
						<div className="absolute inset-0 flex items-center">
							<div className="w-full border-t border-gray-200" />
						</div>
						<div className="relative flex justify-center text-sm">
							<span className="px-4 bg-gradient-to-r from-gray-50 to-gray-100 text-gray-500">
								Of log in met
							</span>
						</div>
					</div>

					<div className="grid grid-cols-2 gap-3">
						{providerNames.map((providerName) => (
							<ProviderConnectionForm
								key={providerName}
								type="Login"
								providerName={providerName}
								redirectTo={redirectTo}
							/>
						))}
					</div>

					<p className="text-center text-sm text-gray-600">
						Nog geen account?{' '}
						<Link
							to="/signup"
							className="font-medium text-blue-600 hover:text-blue-500 hover:underline"
						>
							Maak er een aan
						</Link>
					</p>

					{/* Security Badge */}
					<div className="flex items-center justify-center gap-2 text-xs text-gray-500">
						<Shield className="h-4 w-4" />
						<span>Beveiligde verbinding • 256-bit encryptie</span>
					</div>
				</motion.div>
			</div>

			{/* Right Panel - Beautiful Background with Testimonials */}
			<div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
				{/* Gradient Background */}
				<div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700" />

				{/* Pattern Overlay */}
				<div className="absolute inset-0 opacity-10">
					<div className="absolute inset-0" style={{
						backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
					}} />
				</div>

				{/* Content */}
				<div className="relative z-10 flex flex-col justify-between p-12 text-white">
					<div>
						<h2 className="text-4xl font-bold mb-4">
							Renoveren was nog nooit zo makkelijk
						</h2>
						<p className="text-xl text-white/90 mb-8">
							Sluit je aan bij duizenden huiseigenaren die hun droomhuis creëren met Woonplan
						</p>

						{/* Stats */}
						<div className="grid grid-cols-3 gap-8 mb-12">
							{stats.map((stat, i) => (
								<motion.div
									key={stat.label}
									initial={{ opacity: 0, y: 20 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ delay: i * 0.1 }}
									className="text-center"
								>
									<p className="text-3xl font-bold">{stat.value}</p>
									<p className="text-sm text-white/80">{stat.label}</p>
								</motion.div>
							))}
						</div>
					</div>

					{/* Testimonials Carousel */}
					<div className="bg-white/10 backdrop-blur-md rounded-2xl p-8">
						<motion.div
							key={currentTestimonial}
							initial={{ opacity: 0, x: 20 }}
							animate={{ opacity: 1, x: 0 }}
							exit={{ opacity: 0, x: -20 }}
							transition={{ duration: 0.5 }}
							className="space-y-4"
						>
							<div className="flex items-start gap-2">
								<span className="text-4xl text-white/60">"</span>
								<p className="text-lg italic text-white/90 flex-1">
									{testimonials[currentTestimonial]?.quote}
								</p>
							</div>
							<div className="flex items-center justify-between">
								<div>
									<p className="font-semibold">{testimonials[currentTestimonial]?.author}</p>
									<p className="text-sm text-white/70">
										{testimonials[currentTestimonial]?.location} • {testimonials[currentTestimonial]?.project}
									</p>
								</div>
								<div className="flex gap-1">
									{testimonials.map((_, i) => (
										<button
											key={i}
											onClick={() => setCurrentTestimonial(i)}
											className={`w-2 h-2 rounded-full transition-all ${
												i === currentTestimonial
													? 'bg-white w-8'
													: 'bg-white/40 hover:bg-white/60'
											}`}
										/>
									))}
								</div>
							</div>
						</motion.div>
					</div>

					{/* Features */}
					<div className="grid grid-cols-2 gap-4">
						<div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
							<Sparkles className="h-5 w-5 flex-shrink-0" />
							<div>
								<p className="font-semibold text-sm">AI-gestuurde suggesties</p>
								<p className="text-xs text-white/70">Slimme aanbevelingen</p>
							</div>
						</div>
						<div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
							<Building className="h-5 w-5 flex-shrink-0" />
							<div>
								<p className="font-semibold text-sm">Betrouwbare aannemers</p>
								<p className="text-xs text-white/70">Geverifieerde professionals</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}